import type { TOC } from '@ember/component/template-only';
import { get } from '@ember/helper';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didUpdate } from '@ember/render-modifiers';
import { PrismLoadingBar } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { gt, or } from 'ember-truth-helpers';
import GeneralDatepickersDatepickerDisabled from 'lumen/components/general/datepickers/datepicker-disabled';
import GeneralDropdownsDropdownExport from 'lumen/components/general/dropdowns/dropdown-export';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import GeneralListsListSingleExpand from 'lumen/components/general/lists/list-single-expand';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import GeneralReportScheduleReportsModals from 'lumen/components/general/report-schedule/reports-modals';
import GeneralTablesIssueTableOverview from 'lumen/components/general/tables/issue-table-overview';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import ParityButtonGroup from 'lumen/components/parity/button-group';
import type HotelParityOverviewController from 'lumen/controllers/hotel/parity/overview';
import can from 'lumen/helpers/can';

interface HotelParityOverviewSignature {
  Args: {
    controller: HotelParityOverviewController;
    model: unknown;
  };
}

<template>
  <GeneralPageHeader>
    <div class="content-wrapper">
      <div class="flex flex-row justify-between">
        <div class="flex gap-400">
          <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
          <GeneralDatepickersDatepickerDisabled
            @placeholder={{t "general.next_days" count=@controller.shopDays}}
          />
        </div>
        <div class="rates-navbar">
          <div class="ota-selections-container rates-navbar-scroller gap-200">
            <div class="select-wrapper">
              <div class="no-filter-content">
                {{t "rates.lowest"}}
              </div>
            </div>
            <div class="select-wrapper">
              <div class="no-filter-content">
                {{t "rates.nights" count=@controller.los}}
              </div>
            </div>
            <div class="select-wrapper">
              <div class="no-filter-content">
                {{t "rates.persons_selection" count=@controller.maxPersons}}
              </div>
            </div>
            <div class="select-wrapper">
              <div class="no-filter-content">
                {{t "rates.meal.any"}}
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-center gap-200">
          <ParityButtonGroup />
          <GeneralDropdownsDropdownExport
            @export={{@controller.handleExport}}
            @exportChoices={{@controller.exportChoices}}
            @isScheduleDisabled={{true}}
          />
        </div>
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageMain
    {{didUpdate @controller.paritiesObserver @controller.parities}}
  >
    <div class="content-wrapper main-content">
      {{#if @controller.isLoading}}
        <div class="loading-bar-container">
          <PrismLoadingBar />
        </div>
      {{else}}
        <section id="parity-categories-loss">
          <div class="flex items-center justify-between">
            <h2 class="text-neutral-emphasis mr-200 flex items-center">
              <div class="flex items-center gap-100">
                {{t "parity.estimated_loss"}}
                :&nbsp;
                {{#if @controller.model.isLoadingParityData}}
                  <div class="parity-section-loading-bar-container">
                    <div class="table-loading-state-bar"></div>
                  </div>
                {{else}}
                  <span class="parity-loss">
                    {{@controller.formattedTotalLoss}}
                  </span>
                  <GeneralIconWithTooltip
                    @side="right"
                    @size="medium"
                    @tooltipContent={{t "parity.overview.loss_description"}}
                  />
                {{/if}}
              </div>
            </h2>
          </div>
          {{#if
            (or
              @controller.model.isLoadingParityData
              @controller.lossIssuesPerCategory.length
            )
          }}
            <GeneralListsListSingleExpand
              @expandComponent={{GeneralTablesIssueTableOverview}}
              @isLoading={{@controller.model.isLoadingParityData}}
              @options={{@controller.lossIssuesPerCategory}}
            />
          {{else}}
            <div class="parity-overview-no-data">
              {{t "parity.overview.no_loss" days=@controller.shopDays}}
            </div>
          {{/if}}
        </section>
        <section id="parity-categories-win">
          <div class="flex items-center justify-between">
            <h2 class="text-neutral-emphasis mr-200 flex items-center">
              <div class="flex items-center gap-100">
                {{t "parity.estimated_win"}}
                :&nbsp;
                {{#if @controller.model.isLoadingParityData}}
                  <div class="parity-section-loading-bar-container">
                    <div class="table-loading-state-bar"></div>
                  </div>
                {{else}}
                  <span class="parity-win">
                    {{@controller.formattedTotalWin}}
                  </span>
                  <GeneralIconWithTooltip
                    @side="right"
                    @size="medium"
                    @tooltipContent={{t "parity.overview.win_description"}}
                  />
                {{/if}}
              </div>
            </h2>
          </div>
          {{#if
            (or
              @controller.model.isLoadingParityData
              @controller.winIssuesPerCategory.length
            )
          }}
            <GeneralListsListSingleExpand
              @expandComponent={{GeneralTablesIssueTableOverview}}
              @isLoading={{@controller.model.isLoadingParityData}}
              @options={{@controller.winIssuesPerCategory}}
            />
          {{else}}
            <div class="parity-overview-no-data">
              {{t "parity.overview.no_win" days=@controller.shopDays}}
            </div>
          {{/if}}
        </section>
        {{! @glint-expect-error: Incorrect type }}
        <GeneralReportScheduleReportsModals
          @canCreateNewUser={{can "hotel.reports.create.other.new"}}
          @canCreateOtherReports={{can "hotel.reports.create.other.existing"}}
          @compsetAvailable={{gt
            @controller.hotelAndMarketService.availableCompsets.length
            1
          }}
          @isShowingScheduleModal={{@controller.scheduleReport}}
          @los={{get
            @controller.hotelAndMarketService.hotel.defaultLosParity
            "0"
          }}
          @maxPersons={{@controller.maxPersons}}
          @onCloseScheduleModal={{@controller.closeScheduleModal}}
          @type="parity"
        />
      {{/if}}
    </div>
  </GeneralPageMain>
</template> satisfies TOC<HotelParityOverviewSignature>;
